/* eslint-disable sonarjs/cognitive-complexity, complexity -- bidirectional API<->grid field mapping is inherently long and branchy; splitting it would fragment the 1:1 field correspondence without reducing real complexity */
/**
 * Transform functions for Individual Disability benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields)
 * - Backend API format (nested structures)
 */

import {
  cleanupNotDisclosedBenefits,
  shouldIncludeBenefitInApi,
  transformPreExistingConditionFromApi,
  transformPreExistingConditionToApi,
} from "../shared/builders";

import type {
  IndividualDisabilityBenefitsApi,
  IndividualDisabilityBenefitsGrid,
} from "./types";

/**
 * Transforms Individual Disability benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures.
 *
 * @param benefits - The benefits object from the frontend grid
 * @returns Transformed benefits object matching the API structure
 */
export const transformIndividualDisabilityBenefitsToApi = (
  benefits: Partial<IndividualDisabilityBenefitsGrid>,
): IndividualDisabilityBenefitsApi => {
  const transformed: IndividualDisabilityBenefitsApi = {
    objectType: "individualDisability",
  };

  // Benefit Duration (nested)
  if (shouldIncludeBenefitInApi(benefits.benefitDurationType)) {
    transformed.benefitDuration = {
      type: benefits.benefitDurationType,
    };
    if (benefits.benefitDurationAmount !== undefined) {
      transformed.benefitDuration.amount = benefits.benefitDurationAmount;
    }
  }

  // Definition of Disability (simple)
  if (shouldIncludeBenefitInApi(benefits.definitionOfDisability)) {
    transformed.definitionOfDisability = benefits.definitionOfDisability;
  }

  // Employer Contribution (simple)
  if (shouldIncludeBenefitInApi(benefits.employerContribution)) {
    transformed.employerContribution = benefits.employerContribution;
  }

  // Pre-Existing Condition (using shared builder with monthsDetail pattern)
  transformed.preExistingCondition = transformPreExistingConditionToApi(
    benefits.preExistingConditionType,
    benefits.preExistingConditionMonthsDetail,
    "none",
    "01/01",
  );

  // LTD Plan Design (nested)
  if (shouldIncludeBenefitInApi(benefits.ltdPlanDesignType)) {
    transformed.ltdPlanDesign = {
      type: benefits.ltdPlanDesignType,
    };
    if (benefits.ltdPlanDesignPercent !== undefined) {
      transformed.ltdPlanDesign.percent = benefits.ltdPlanDesignPercent;
    }
    if (benefits.ltdPlanDesignDollarAmount !== undefined) {
      transformed.ltdPlanDesign.dollarAmount =
        benefits.ltdPlanDesignDollarAmount;
    }
  }

  // IDI Plan Design (nested)
  if (shouldIncludeBenefitInApi(benefits.idiPlanDesignType)) {
    transformed.idiPlanDesign = {
      type: benefits.idiPlanDesignType,
    };
    if (benefits.idiPlanDesignPercent !== undefined) {
      transformed.idiPlanDesign.percent = benefits.idiPlanDesignPercent;
    }
    if (benefits.idiPlanDesignDollarAmount !== undefined) {
      transformed.idiPlanDesign.dollarAmount =
        benefits.idiPlanDesignDollarAmount;
    }
  }

  // GSI Benefit Maximum (simple)
  if (benefits.gsiBenefitMaximum !== undefined) {
    transformed.gsiBenefitMaximum = benefits.gsiBenefitMaximum;
  }

  // Own Occupation Period (nested)
  if (shouldIncludeBenefitInApi(benefits.ownOccupationPeriodType)) {
    transformed.ownOccupationPeriod = {
      type: benefits.ownOccupationPeriodType,
    };
    if (benefits.ownOccupationPeriodAmount !== undefined) {
      transformed.ownOccupationPeriod.amount =
        benefits.ownOccupationPeriodAmount;
    }
  }

  // Elimination Period Days (simple)
  if (benefits.eliminationPeriodDays !== undefined) {
    transformed.eliminationPeriodDays = benefits.eliminationPeriodDays;
  }

  // Mental Illness Benefit (nested)
  if (shouldIncludeBenefitInApi(benefits.mentalIllnessBenefitType)) {
    transformed.mentalIllnessBenefit = {
      type: benefits.mentalIllnessBenefitType,
    };
    if (benefits.mentalIllnessBenefitTimeframe !== undefined) {
      transformed.mentalIllnessBenefit.timeframe =
        benefits.mentalIllnessBenefitTimeframe;
    }
  }

  // Recovery Benefit (nested)
  if (shouldIncludeBenefitInApi(benefits.recoveryBenefitType)) {
    transformed.recoveryBenefit = {
      type: benefits.recoveryBenefitType,
    };
    if (benefits.recoveryBenefitAmount !== undefined) {
      transformed.recoveryBenefit.amount = benefits.recoveryBenefitAmount;
    }
  }

  // Catastrophic Benefit (nested)
  if (shouldIncludeBenefitInApi(benefits.catastrophicBenefitType)) {
    transformed.catastrophicBenefit = {
      type: benefits.catastrophicBenefitType,
    };
    if (benefits.catastrophicBenefitAmount !== undefined) {
      transformed.catastrophicBenefit.amount =
        benefits.catastrophicBenefitAmount;
    }
  }

  // Portability (simple)
  if (shouldIncludeBenefitInApi(benefits.portability)) {
    transformed.portability = benefits.portability;
  }

  // Benefit Class Availability (simple)
  if (benefits.benefitClassAvailability !== undefined) {
    transformed.benefitClassAvailability = benefits.benefitClassAvailability;
  }

  return cleanupNotDisclosedBenefits(
    transformed,
  ) as IndividualDisabilityBenefitsApi;
};

/**
 * Transforms Individual Disability benefits from API format to frontend grid format
 *
 * Converts nested API structures into flat grid fields.
 *
 * @param benefits - The benefits object from the API
 * @returns Transformed benefits object matching the grid structure
 */
export const transformIndividualDisabilityBenefitsFromApi = (
  benefits: Partial<IndividualDisabilityBenefitsApi>,
): IndividualDisabilityBenefitsGrid => {
  const transformed: IndividualDisabilityBenefitsGrid = {
    objectType: "individualDisability",
  };

  // Benefit Duration (flatten nested)
  if (benefits.benefitDuration) {
    if (benefits.benefitDuration.type !== undefined) {
      transformed.benefitDurationType = benefits.benefitDuration.type;
    }
    if (benefits.benefitDuration.amount !== undefined) {
      transformed.benefitDurationAmount = benefits.benefitDuration.amount;
    }
  }

  // Definition of Disability (simple)
  if (benefits.definitionOfDisability !== undefined) {
    transformed.definitionOfDisability = benefits.definitionOfDisability;
  }

  // Employer Contribution (simple)
  if (benefits.employerContribution !== undefined) {
    transformed.employerContribution = benefits.employerContribution;
  }

  // Pre-Existing Condition (using shared builder to format monthsDetail)
  const preExistingConditionFields = transformPreExistingConditionFromApi(
    benefits.preExistingCondition,
  );
  transformed.preExistingConditionType =
    preExistingConditionFields.preExistingConditionType;
  transformed.preExistingConditionMonthsDetail =
    preExistingConditionFields.preExistingConditionMonthsDetail;

  // LTD Plan Design (flatten nested)
  if (benefits.ltdPlanDesign) {
    if (benefits.ltdPlanDesign.type !== undefined) {
      transformed.ltdPlanDesignType = benefits.ltdPlanDesign.type;
    }
    if (benefits.ltdPlanDesign.percent !== undefined) {
      transformed.ltdPlanDesignPercent = benefits.ltdPlanDesign.percent;
    }
    if (benefits.ltdPlanDesign.dollarAmount !== undefined) {
      transformed.ltdPlanDesignDollarAmount =
        benefits.ltdPlanDesign.dollarAmount;
    }
  }

  // IDI Plan Design (flatten nested)
  if (benefits.idiPlanDesign) {
    if (benefits.idiPlanDesign.type !== undefined) {
      transformed.idiPlanDesignType = benefits.idiPlanDesign.type;
    }
    if (benefits.idiPlanDesign.percent !== undefined) {
      transformed.idiPlanDesignPercent = benefits.idiPlanDesign.percent;
    }
    if (benefits.idiPlanDesign.dollarAmount !== undefined) {
      transformed.idiPlanDesignDollarAmount =
        benefits.idiPlanDesign.dollarAmount;
    }
  }

  // GSI Benefit Maximum (simple)
  if (benefits.gsiBenefitMaximum !== undefined) {
    transformed.gsiBenefitMaximum = benefits.gsiBenefitMaximum;
  }

  // Own Occupation Period (flatten nested)
  if (benefits.ownOccupationPeriod) {
    if (benefits.ownOccupationPeriod.type !== undefined) {
      transformed.ownOccupationPeriodType = benefits.ownOccupationPeriod.type;
    }
    if (benefits.ownOccupationPeriod.amount !== undefined) {
      transformed.ownOccupationPeriodAmount =
        benefits.ownOccupationPeriod.amount;
    }
  }

  // Elimination Period Days (simple)
  if (benefits.eliminationPeriodDays !== undefined) {
    transformed.eliminationPeriodDays = benefits.eliminationPeriodDays;
  }

  // Mental Illness Benefit (flatten nested)
  if (benefits.mentalIllnessBenefit) {
    if (benefits.mentalIllnessBenefit.type !== undefined) {
      transformed.mentalIllnessBenefitType = benefits.mentalIllnessBenefit.type;
    }
    if (benefits.mentalIllnessBenefit.timeframe !== undefined) {
      transformed.mentalIllnessBenefitTimeframe =
        benefits.mentalIllnessBenefit.timeframe;
    }
  }

  // Recovery Benefit (flatten nested)
  if (benefits.recoveryBenefit) {
    if (benefits.recoveryBenefit.type !== undefined) {
      transformed.recoveryBenefitType = benefits.recoveryBenefit.type;
    }
    if (benefits.recoveryBenefit.amount !== undefined) {
      transformed.recoveryBenefitAmount = benefits.recoveryBenefit.amount;
    }
  }

  // Catastrophic Benefit (flatten nested)
  if (benefits.catastrophicBenefit) {
    if (benefits.catastrophicBenefit.type !== undefined) {
      transformed.catastrophicBenefitType = benefits.catastrophicBenefit.type;
    }
    if (benefits.catastrophicBenefit.amount !== undefined) {
      transformed.catastrophicBenefitAmount =
        benefits.catastrophicBenefit.amount;
    }
  }

  // Portability (simple)
  if (benefits.portability !== undefined) {
    transformed.portability = benefits.portability;
  }

  // Benefit Class Availability (simple)
  if (benefits.benefitClassAvailability !== undefined) {
    transformed.benefitClassAvailability = benefits.benefitClassAvailability;
  }

  return transformed;
};
